import { Edit, InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material'
import { Tooltip } from '@mui/material'
import { LocationHourResource, LocationHourUpsertInput } from 'api'
import { Availabilities } from 'api/menuMapping/types'
import { getLocationHours, upsertLocationHours } from 'api/partners/locationHours'
import { ChilipiperSection, LoadingSkeleton } from 'components'
import validateAvailabilities from 'features/manage-locations/IndividualMenu/util/validateAvailabilities'
import { useGetLocationPartners } from 'hooks'
import { sortBy, startCase } from 'lodash'
import moment from 'moment'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { FormattedAvailabilities } from 'types'
import { DAYS_OF_WEEK } from 'utils/DateTime'
import {
  SectionContent,
  SectionEditIcon,
  SectionHeader,
  SectionTitle,
  SectionTitleContainer,
  SectionWrapper,
} from './../styles'
import LocationHoursForm from './LocationHoursForm'
import LocationHoursList from './LocationHoursList'

type Props = {
  partnerId: string
  isEditing: boolean
  setIsEditing: (isEditing: boolean) => void
}

const transformLocationHours = (data: LocationHourResource[]): Availabilities[] => {
  data.forEach((item) => {
    if (item.attributes.time_periods.length > 1) {
      item.attributes.time_periods = sortBy(item.attributes.time_periods, (timePeriod) =>
        moment(timePeriod.start_time, 'HH:mm').toDate(),
      )
    }
  })

  return data.map((item) => ({
    id: item.id,
    dayOfWeek: startCase(item.attributes.day_of_week),
    timePeriods: item.attributes.time_periods.map((period, index) => ({
      id: item.relationships.time_periods.data[index]?.id || '',
      attributes: {
        start_time: period.start_time,
        end_time: period.end_time,
      },
    })),
    hours: item.attributes.time_periods
      .map(
        (period) =>
          `${moment(period.start_time, 'HH:mm').format('h:mmA')} - ${moment(
            period.end_time,
            'HH:mm',
          ).format('h:mmA')}`,
      )
      .join(', ') as string,
    enabled: item.attributes.enabled,
  })) as Availabilities[]
}

const transformAvailabilitiesToLocationHours = (
  availabilities: FormattedAvailabilities,
): LocationHourUpsertInput => {
  const daysOfWeek = Object.keys(availabilities)

  const data = daysOfWeek.map((day) => ({
    type: 'location_hour' as const,
    attributes: {
      day_of_week: day.toLowerCase(),
      enabled: availabilities[day].length > 0,
      time_periods: availabilities[day]
        .filter((period) => period.start_time && period.end_time)
        .map((period) => ({
          start_time: period.start_time as string,
          end_time: period.end_time as string,
        })),
    },
  }))

  return { data }
}

const LocationHours = ({ partnerId, isEditing, setIsEditing }: Props) => {
  const [locationHours, setLocationHours] = useState<Availabilities[]>([])
  const [newAvailabilities, setNewAvailabilities] = useState<FormattedAvailabilities>({})
  const [availabilitiesToDelete, setAvailabilitiesToDelete] = useState<Availabilities['id'][] | []>(
    [],
  )
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const { organization } = useGetLocationPartners(partnerId)
  const isRCCPlatform = organization?.attributes?.chowly_platform ?? null
  const isReadyForEdit = Object.keys(newAvailabilities).length === DAYS_OF_WEEK.length
  const timePeriodsError = validateAvailabilities(newAvailabilities) === false

  const fetchLocationHours = useCallback(async () => {
    try {
      if (!partnerId) return
      setIsLoading(true)
      const response = await getLocationHours(partnerId)
      setLocationHours(transformLocationHours(response?.data))
    } catch (error) {
      console.error('Failed to fetch location hours:', error)
      toast.error('Failed to load location hours.')
    } finally {
      setIsLoading(false)
    }
  }, [partnerId])

  const saveLocationHours = async () => {
    if (!partnerId) return
    setIsSaving(true)

    try {
      const result = await upsertLocationHours(
        partnerId,
        transformAvailabilitiesToLocationHours(newAvailabilities),
      )

      if (result) {
        toast.success('Success! Hours have been updated.', { autoClose: 5000 })
        fetchLocationHours()
        setIsSaving(false)
        setIsEditing(false)
      }
    } catch (error) {
      console.error('Failed to save location hours:', error)
      toast.error('Failed to save location hours.')
    } finally {
      setIsSaving(false)
    }
  }

  useEffect(() => {
    const formattedAvailabilities: FormattedAvailabilities = {}

    DAYS_OF_WEEK.forEach((day) => {
      const existingAvailability = [...locationHours].find((avail) => avail.dayOfWeek === day)

      if (existingAvailability?.enabled) {
        formattedAvailabilities[existingAvailability.dayOfWeek] =
          existingAvailability.timePeriods.map((timePeriod) => ({
            id: timePeriod.id,
            start_time: timePeriod.attributes.start_time,
            end_time: timePeriod.attributes.end_time,
          }))
      } else {
        formattedAvailabilities[day] = []
      }
    })

    setNewAvailabilities(formattedAvailabilities)
  }, [locationHours])

  useEffect(() => {
    if (!partnerId) return
    fetchLocationHours()
  }, [partnerId, fetchLocationHours])

  const isPending = isLoading || isSaving || isRCCPlatform == null || (isEditing && !isReadyForEdit)

  return (
    <SectionWrapper isEditingMode={isEditing}>
      <SectionHeader>
        <SectionTitleContainer>
          <SectionTitle>Location Hours</SectionTitle>
          <Tooltip
            title='Updating hours below will send updated hours to your Google Business Profile and your website.'
            arrow
          >
            <InfoOutlinedIcon
              style={{
                marginLeft: '8px',
                color: '#777777',
                fontSize: '20px',
                cursor: 'pointer',
              }}
            />
          </Tooltip>
        </SectionTitleContainer>
        {!isPending && !isEditing && isRCCPlatform && (
          <Tooltip title='Edit location hours' arrow>
            <SectionEditIcon onClick={() => setIsEditing(true)}>
              <Edit />
            </SectionEditIcon>
          </Tooltip>
        )}
      </SectionHeader>
      <SectionContent>
        {isPending ? (
          <LoadingSkeleton isFlex />
        ) : (
          <>
            {!isRCCPlatform ? (
              <ChilipiperSection
                message='To manage your locations on your Google Business Profile and Marketing Website, click below to learn more'
                actionBtnText='Get in touch'
              />
            ) : isEditing ? (
              <LocationHoursForm
                availabilities={locationHours}
                newAvailabilities={newAvailabilities}
                setNewAvailabilities={setNewAvailabilities}
                availabilitiesToDelete={availabilitiesToDelete}
                setAvailabilitiesToDelete={setAvailabilitiesToDelete}
                timePeriodsError={timePeriodsError}
                saveButtonDisabled={isSaving}
                onSave={saveLocationHours}
                onCancel={() => setIsEditing(false)}
              />
            ) : (
              <LocationHoursList availabilities={locationHours} />
            )}
          </>
        )}    
      </SectionContent>
    </SectionWrapper>
  )
}

export default LocationHours
